import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { updateUser } from '../../../store/userReducer';
import { getOwnProfile } from '../../../services/selfProfile';
import {
    getSelectedProfileRole,
    profileContextKey,
    profileUserFromState
} from '../../../utils/selfProfile';

export function useProfileAccount() {
    const state = useSelector((current) => current.user);
    const store = useStore();
    const dispatch = useDispatch();
    const client = useQueryClient();
    const user = useMemo(() => profileUserFromState(state), [state]);
    const role = getSelectedProfileRole(state);
    const contextKey = profileContextKey(state);
    const isCurrent = useCallback(
        () => profileContextKey(store.getState().user) === contextKey,
        [store, contextKey]
    );
    const refreshUser = useCallback(async () => {
        if (!isCurrent()) throw new Error('PROFILE_CONTEXT_CHANGED');
        const response = await getOwnProfile(role);
        if (!isCurrent() || response.data?.uuid !== user.uuid)
            throw new Error('PROFILE_CONTEXT_CHANGED');
        // Preserve the Axios response shape used by the medical access guard.
        client.setQueryData(['user'], (cached) =>
            cached?.data?.uuid === user.uuid
                ? { ...cached, data: response.data }
                : undefined
        );
        dispatch(updateUser(response.data));
        return response.data;
    }, [client, dispatch, isCurrent, role, user.uuid]);

    const updateUserPicture = useCallback(
        (picture, userUuid) => {
            const latest = store.getState().user;
            if (
                !isCurrent() ||
                latest.uuid !== userUuid ||
                typeof picture !== 'string' ||
                !picture
            )
                return;
            const raw = { ...profileUserFromState(latest), picture };
            client.setQueryData(['user'], (cached) =>
                cached?.data?.uuid === userUuid
                    ? { ...cached, data: { ...cached.data, picture } }
                    : undefined
            );
            dispatch(updateUser(raw));
        },
        [client, dispatch, isCurrent, store]
    );

    return { user, role, contextKey, refreshUser, updateUserPicture };
}
